// The browser's backing for the shared device library's LocalStore
// (practicemd-api common/device/local_store.go).
//
// WHAT THIS FILE IS ALLOWED TO DECIDE: where the bytes go. Nothing else.
// The schema, the sealing, and every reconcile rule live in common/device,
// so this and the CLI's SQLite backing (practicemd-cli
// cmd/local_store_sqlite.go) cannot come to different conclusions about the
// same cache. The table is deliberately dumb, (bucket, key) → bytes,
// mirroring the CLI's `records(bucket, key, value)`.
//
// One database per origin. IndexedDB is shared by every tab, so the sealed
// tiers (identities, send content) are shared too; only the WASM's memory
// tier is per tab. Registered with the WASM host by loader.ts as soon as
// the practicemdWASM global appears (practicemdWASM.setLocalStore).

import { openDB, IDBPDatabase } from 'idb'

const DB_NAME = 'practicemd-local-store'
const DB_VERSION = 1
const STORE = 'records'

export interface WasmLocalStore {
    get(bucket: string, key: string): Promise<Uint8Array | null>
    put(bucket: string, key: string, value: Uint8Array): Promise<void>
    delete(bucket: string, key: string): Promise<void>
    keys(bucket: string): Promise<string[]>
}

let dbPromise: Promise<IDBPDatabase> | null = null

function open(): Promise<IDBPDatabase> {
    if (!dbPromise) {
        dbPromise = openDB(DB_NAME, DB_VERSION, {
            upgrade(db) {
                if (!db.objectStoreNames.contains(STORE)) {
                    db.createObjectStore(STORE, { keyPath: ['bucket', 'key'] })
                }
            },
        })
        dbPromise.catch(() => {
            dbPromise = null // let a later call retry after a transient failure
        })
    }
    return dbPromise
}

export function hasIndexedDb(): boolean {
    return typeof indexedDB !== 'undefined'
}

export function createIndexedDbLocalStore(): WasmLocalStore {
    return {
        async get(bucket, key) {
            const db = await open()
            const row = await db.get(STORE, [bucket, key])
            if (!row) return null
            const v = row.value
            return v instanceof Uint8Array ? v : new Uint8Array(v)
        },
        async put(bucket, key, value) {
            const db = await open()
            // Copy: the WASM's Uint8Array views its linear memory, which can
            // move on the next allocation; IndexedDB must own its bytes.
            await db.put(STORE, { bucket, key, value: new Uint8Array(value) })
        },
        async delete(bucket, key) {
            const db = await open()
            await db.delete(STORE, [bucket, key])
        },
        async keys(bucket) {
            const db = await open()
            const range = IDBKeyRange.bound([bucket, ''], [bucket, '￿'])
            const ks = await db.getAllKeys(STORE, range)
            return ks.map(k => (k as [string, string])[1]).sort()
        },
    }
}
